"use client";

// Menu de navigation principal — pastilles circulaires qui s'étirent au
// survol et révèlent leur libellé sur un dégradé teinté. Chaque entrée a sa
// propre paire de couleurs, alignée sur la palette Aurora de Kessem.
//
// Structure : `group` Tailwind sur le `<li>`, `width` qui passe de 44 → 160px
// au hover, l'icône scale-down + le libellé scale-up en delay pour donner
// l'impression que la pastille « avale » l'icône puis « expulse » le mot.
// Halo gradient blur additionnel derrière la pastille pour un effet luminescence.

import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { Code2, Mail, Workflow } from "lucide-react";
import { Link, usePathname } from "@/i18n/navigation";

type Item = {
  hash: string;
  key: string;
  icon: React.ComponentType<{ className?: string }>;
  from: string;
  to: string;
};

const ITEMS: Item[] = [
  {
    hash: "services",
    key: "services",
    icon: Code2,
    from: "var(--aurora-cyan)",
    to: "var(--aurora-blue)",
  },
  {
    hash: "process",
    key: "process",
    icon: Workflow,
    from: "var(--aurora-violet)",
    to: "var(--aurora-magenta)",
  },
  {
    hash: "contact",
    key: "contact",
    icon: Mail,
    from: "var(--aurora-blue)",
    to: "var(--aurora-violet)",
  },
];

export function GradientMenu({
  onNavigate,
}: {
  onNavigate?: () => void;
}) {
  const tNav = useTranslations("nav");
  // FAQ doesn't have a "nav" key everywhere; fall back gracefully.
  const labelFor = (key: string): string => {
    try {
      return tNav(key);
    } catch {
      return key;
    }
  };

  const pathname = usePathname();
  const isHome = pathname === "/";
  const [activeHash, setActiveHash] = useState<string | null>(null);

  useEffect(() => {
    if (!isHome) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActiveHash(entry.target.id);
        }
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
    );
    ITEMS.forEach((it) => {
      const el = document.getElementById(it.hash);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isHome]);

  return (
    <ul className="flex items-center gap-3" aria-label="Primary">
      {ITEMS.map((item) => {
        const Icon = item.icon;
        const isActive = isHome && activeHash === item.hash;
        return (
          <li
            key={item.hash}
            className="group relative"
            style={
              {
                ["--mfrom" as string]: item.from,
                ["--mto" as string]: item.to,
              } as React.CSSProperties
            }
          >
            <Link
              href={{ pathname: "/", hash: item.hash }}
              onClick={onNavigate}
              aria-label={labelFor(item.key)}
              className={`relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border bg-card shadow-sm transition-[width,box-shadow,border-color] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:w-[150px] group-hover:border-transparent group-hover:shadow-none focus-visible:w-[150px] focus-visible:border-transparent ${
                isActive
                  ? "border-aurora-cyan/50"
                  : "border-border"
              }`}
            >
              {/* Gradient fill */}
              <span
                aria-hidden
                className="absolute inset-0 rounded-full bg-[linear-gradient(45deg,var(--mfrom),var(--mto))] opacity-0 transition-opacity duration-500 group-hover:opacity-100 group-focus-visible:opacity-100"
              />
              {/* Diffuse glow underneath */}
              <span
                aria-hidden
                className="-z-10 absolute inset-x-0 top-2 h-full rounded-full bg-[linear-gradient(45deg,var(--mfrom),var(--mto))] opacity-0 blur-[14px] transition-opacity duration-500 group-hover:opacity-55 group-focus-visible:opacity-55"
              />
              {/* Active indicator dot (when section in view, before hover) */}
              {isActive && (
                <span
                  aria-hidden
                  className="absolute bottom-1 left-1/2 size-1 -translate-x-1/2 rounded-full bg-aurora-cyan transition-opacity duration-300 group-hover:opacity-0"
                />
              )}
              {/* Icon — fades + scales out on hover */}
              <span className="relative z-10 inline-flex transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-0 group-focus-visible:scale-0">
                <Icon className="size-[18px] text-foreground/75" />
              </span>
              {/* Label — scales in with a slight delay for the "expel" feel */}
              <span className="pointer-events-none absolute z-10 scale-0 whitespace-nowrap font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-transform duration-500 delay-100 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-100 group-focus-visible:scale-100">
                {labelFor(item.key)}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
